import { Stack } from '@mui/material';
import { Img } from 'components/common';
import { FC, useEffect, useState } from 'react';
import { Segment } from 'types';
import utils from 'utils';

type Props = {
  list: string[];
  segment: Segment;
  crvCode: string;
};

const getStampUrl = (segment: Segment, stampName: string) =>
  `https://firebasestorage.googleapis.com/v0/b/crv-lagoa.appspot.com/o/assets%2Fselos%2F${utils.segmentTranslator(
    segment,
    'snake'
  )}%2F${stampName}.png?alt=media`;

const getBullCodeStampNames = (segment: Segment, crvCode: string) => {
  if (!crvCode) return [];
  return segment === 'zl' ? [ `${crvCode}pmgz`, crvCode ] : [ crvCode ];
};

const imageExists = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const StampList: FC<Props> = ({ list, segment, crvCode }) => {
  const bullCodeStampNames = getBullCodeStampNames(segment, crvCode);
  const normalizedBullCodeStampNames = bullCodeStampNames.map((stamp) =>
    stamp.toLowerCase()
  );
  const [ bullCodeStampName, setBullCodeStampName ] = useState<string | null>(
    null
  );
  const regularStamps = list.filter(
    (stamp) => !normalizedBullCodeStampNames.includes(stamp.toLowerCase())
  );

  useEffect(() => {
    let isMounted = true;

    const loadBullCodeStamp = async () => {
      setBullCodeStampName(null);

      for (const stampName of bullCodeStampNames) {
        const exists = await imageExists(getStampUrl(segment, stampName));
        if (!isMounted) return;
        if (exists) {
          setBullCodeStampName(stampName);
          return;
        }
      }
    };

    loadBullCodeStamp();

    return () => {
      isMounted = false;
    };
  }, [ segment, crvCode ]);

  return (
    <Stack
      {...utils.stack.flex.row('flex-start', 'center')}
      spacing={1}
      sx={{ width: '100%', overflowX: 'auto', marginBottom: '24px' }}
    >
      {regularStamps.map((n) => {
        const result = n.includes('--baixo') ? n.replace('--baixo', '') : n;

        return (
          <Img
            key={n}
            url={getStampUrl(segment, result)}
            fallbackImg="/imgs/default.png"
            sx={{ height: '50px' }}
            rmIfFail
          />
        );
      })}
      {bullCodeStampName && (
        <Img
          key={bullCodeStampName}
          url={getStampUrl(segment, bullCodeStampName)}
          sx={{ height: '50px' }}
          rmIfFail
        />
      )}
    </Stack>
  );
};

export default StampList;
